import { useState, useEffect } from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
// CORRECCIÓN: Usamos rutas relativas para evitar errores de compilación con alias
import { auth, googleProvider, db } from '../config/firebase';
import type { User, AuthState } from '../types';
import { checkInvitation, deleteInvitation } from '../services/firestoreService';

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isApproved: false,
  });

  // Función para obtener datos del usuario desde Firestore
  const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Usuario nuevo: Verificar si tiene una invitación pendiente
        const invitation = await checkInvitation(firebaseUser.email!);

        const baseUser: User = {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || undefined,
          photoURL: firebaseUser.photoURL || undefined,
        };

        const newUser: User = invitation
          ? {
              ...baseUser,
              role: invitation.role,
              ...(invitation.access_permissions ? { access_permissions: invitation.access_permissions } : {}),
            }
          : baseUser;

        // Firestore no permite undefined, así que solo persistimos campos definidos.
        const userDocData: any = {
          uid: newUser.uid,
          email: newUser.email,
          ...(newUser.displayName ? { displayName: newUser.displayName } : {}),
          ...(newUser.photoURL ? { photoURL: newUser.photoURL } : {}),
          ...(newUser.role ? { role: newUser.role } : {}),
          ...(newUser.access_permissions ? { access_permissions: newUser.access_permissions } : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Primero creamos el documento del usuario para asegurar que no se pierda la invitación si falla la escritura
        await setDoc(userRef, userDocData);

        // Si todo salió bien, ahora sí eliminamos la invitación
        if (invitation) {
          await deleteInvitation(invitation.id);
        }

        return newUser;
      }

      const userData = userDoc.data() as User;
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        displayName: firebaseUser.displayName || userData.displayName,
        photoURL: firebaseUser.photoURL || userData.photoURL,
        role: userData.role,
        access_permissions: userData.access_permissions,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  };

  // Verificar si el usuario está aprobado (tiene rol asignado)
  const checkUserApproval = (user: User): boolean => {
    if (user.role === 'manager') return true;
    if (user.role === 'innovation') return true;
    if (user.role === 'innovation++') return true;
    return !!(user.role && user.access_permissions);
  };

  // Efecto para escuchar cambios en la autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          setAuthState((prev) => ({ ...prev, loading: true, error: null }));

          const userProfile = await fetchUserProfile(firebaseUser);

          if (!userProfile) {
            throw new Error('No se pudo cargar el perfil del usuario');
          }

          const isApproved = checkUserApproval(userProfile);

          setAuthState({
            user: userProfile,
            loading: false,
            error: null,
            isApproved,
          });
        } catch (error) {
          console.error('Error al cargar perfil de usuario:', error);
          setAuthState({
            user: null,
            loading: false,
            error: 'Error al cargar el perfil del usuario',
            isApproved: false,
          });
        }
      } else {
        // No hay usuario autenticado
        setAuthState({
          user: null,
          loading: false,
          error: null,
          isApproved: false,
        });
      }
    });

    // Cleanup
    return () => unsubscribe();
  }, []);

  // Función para registrar usuario con Email/Password
  const registerWithEmail = async (email: string, password: string) => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));
      // Esto disparará onAuthStateChanged, que a su vez llamará a fetchUserProfile
      await createUserWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error: any) {
      console.error('Error en registro:', error);
      let errorMessage = 'Error al registrarse';
      if (error.code === 'auth/email-already-in-use') errorMessage = 'Este email ya está en uso';
      else if (error.code === 'auth/weak-password') errorMessage = 'La contraseña es muy débil';
      else if (error.code === 'auth/invalid-email') errorMessage = 'Email inválido';

      setAuthState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  // Función para iniciar sesión con Google
  const signInWithGoogle = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      const userProfile = await fetchUserProfile(firebaseUser);

      if (!userProfile) {
        throw new Error('No se pudo crear el perfil del usuario');
      }

      const isApproved = checkUserApproval(userProfile);

      setAuthState({
        user: userProfile,
        loading: false,
        error: null,
        isApproved,
      });

      return { success: true, isApproved };
    } catch (error: any) {
      console.error('Error en sign in:', error);

      let errorMessage = 'Error al iniciar sesión';

      // RESTAURADO: Manejo detallado de errores de Google
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Inicio de sesión cancelado';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup bloqueado por el navegador';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Solicitud cancelada';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Inicio de sesión con Google no habilitado';
      } else if (error.code === 'auth/invalid-argument') {
        errorMessage = typeof error.message === 'string' && error.message.trim()
          ? error.message
          : 'Parámetros inválidos al iniciar sesión';
      } else if (error.code === 'auth/internal-error') {
        errorMessage = typeof error.message === 'string' && error.message.trim()
          ? error.message
          : 'Error interno al iniciar sesión';
      } else if (typeof error.message === 'string' && error.message.includes('Dominio')) {
        errorMessage = error.message;
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      return { success: false, isApproved: false, error: errorMessage };
    }
  };

  // Función para iniciar sesión con Email/Password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      const result = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;

      const userProfile = await fetchUserProfile(firebaseUser);

      if (!userProfile) {
        throw new Error('No se pudo obtener el perfil del usuario');
      }

      const isApproved = checkUserApproval(userProfile);

      setAuthState({
        user: userProfile,
        loading: false,
        error: null,
        isApproved,
      });

      return { success: true, isApproved };
    } catch (error: any) {
      console.error('Error en email sign in:', error);

      let errorMessage = 'Error al iniciar sesión';

      // RESTAURADO: Manejo detallado de errores de Email/Password
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Usuario deshabilitado';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Usuario no encontrado';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Contraseña incorrecta';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Credenciales inválidas';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Inicio de sesión con Email/Password no habilitado';
      } else if (error.code === 'auth/invalid-argument') {
        errorMessage = typeof error.message === 'string' && error.message.trim()
          ? error.message
          : 'Parámetros inválidos al iniciar sesión';
      } else if (error.code === 'auth/internal-error') {
        errorMessage = typeof error.message === 'string' && error.message.trim()
          ? error.message
          : 'Error interno al iniciar sesión';
      } else if (typeof error.message === 'string' && error.message.includes('Dominio')) {
        errorMessage = error.message;
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      return { success: false, isApproved: false, error: errorMessage };
    }
  };

  // Función para cerrar sesión
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setAuthState({
        user: null,
        loading: false,
        error: null,
        isApproved: false,
      });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      setAuthState((prev) => ({
        ...prev,
        error: 'Error al cerrar sesión',
      }));
    }
  };

  // Función para refrescar el perfil del usuario
  const refreshUserProfile = async () => {
    if (!auth.currentUser) return;

    try {
      setAuthState((prev) => ({ ...prev, loading: true }));

      const userProfile = await fetchUserProfile(auth.currentUser);

      if (userProfile) {
        const isApproved = checkUserApproval(userProfile);

        setAuthState({
          user: userProfile,
          loading: false,
          error: null,
          isApproved,
        });
      }
    } catch (error) {
      console.error('Error al refrescar perfil:', error);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: 'Error al refrescar el perfil',
      }));
    }
  };

  // Función para limpiar errores
  const clearError = () => {
    setAuthState((prev) => ({ ...prev, error: null }));
  };

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    isApproved: authState.isApproved,
    isAuthenticated: !!authState.user,
    isManager: authState.user?.role === 'manager',
    isInnovation: authState.user?.role === 'innovation',
    isInnovationPlusPlus: authState.user?.role === 'innovation++',
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    signOut,
    refreshUserProfile,
    clearError,
  };
}
